/**
 * Messagerie interne : formateur ↔ surveillance / direction / scolarité.
 * Conversation threadée.
 */
import { useState, useEffect, useRef } from 'react';
import {
  MessagesSquare, Loader2, Plus, X, Send, ChevronLeft, Building2, Shield, ClipboardList,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useMesMessagesInternesQuery, useThreadMessageInterneQuery,
  useEnvoyerMessageInterneMutation,
} from '../../services/formateurApi';

type DestRole = 'surveillance' | 'direction' | 'scolarite';

const DEST_META: Record<DestRole, { label: string; Icon: any; color: string }> = {
  surveillance: { label: 'Surveillance', Icon: ClipboardList, color: 'amber' },
  direction:    { label: 'Direction',    Icon: Building2,     color: 'red'   },
  scolarite:    { label: 'Scolarité',    Icon: Shield,        color: 'cyan'  },
};

export default function MessagerieInterne() {
  const { data, isLoading, refetch } = useMesMessagesInternesQuery();
  const [showNouveau, setShowNouveau] = useState(false);
  const [threadOuvert, setThreadOuvert] = useState<number | null>(null);

  if (threadOuvert !== null) {
    return <VueThread threadId={threadOuvert}
      onBack={() => { setThreadOuvert(null); refetch(); }} />;
  }

  return (
    <FormateurLayout title="Messagerie interne">
      <button onClick={() => setShowNouveau(true)}
        className="w-full mb-3 bg-gradient-to-br from-red-900/40 to-red-900/10 hover:border-red-500
          border border-red-700/40 rounded-xl p-3 flex items-center gap-3 transition-colors text-left">
        <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center text-red-300">
          <Plus size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Nouveau message</div>
          <div className="text-[11px] text-gray-400">Surveillance, direction ou scolarité</div>
        </div>
      </button>

      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : (data?.results || []).length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          <MessagesSquare size={32} className="inline mb-2 opacity-50" />
          <div>Aucune conversation pour l'instant.</div>
        </div>
      ) : (
        (() => {
          // Groupement par destinataire_role (Surveillance / Direction / Scolarité)
          const ordre: DestRole[] = ['surveillance', 'direction', 'scolarite'];
          const groupes: Record<string, NonNullable<typeof data>['results']> = {};
          for (const t of data!.results) {
            const k = (t.destinataire_role as DestRole) || 'autre';
            (groupes[k] ||= []).push(t);
          }
          const cles = [...ordre.filter(k => groupes[k]?.length),
                        ...Object.keys(groupes).filter(k => !ordre.includes(k as DestRole))];
          return (
            <div className="space-y-4">
              {cles.map(k => {
                const liste = groupes[k];
                const meta = DEST_META[k as DestRole] || { label: k, Icon: MessagesSquare, color: 'gray' };
                const nbNonLus = liste.reduce((acc: number, t) => acc + t.nb_non_lus, 0);
                return (
                  <div key={k}>
                    <div className="flex items-center gap-2 mb-1.5 px-1">
                      <meta.Icon size={12} className={`text-${meta.color}-400`} />
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-300">
                        {meta.label}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {liste.length} conv.
                      </span>
                      {nbNonLus > 0 && (
                        <span className="text-[9px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                          {nbNonLus} non lu{nbNonLus > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {liste.map(t => (
                        <button key={t.thread_id} onClick={() => setThreadOuvert(t.thread_id)}
                          className="w-full bg-gray-900 border border-gray-800 hover:border-amber-700/60 rounded-lg p-3 text-left transition">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="text-sm font-semibold text-white truncate flex-1">
                              {t.sujet}
                            </div>
                            {t.nb_non_lus > 0 && (
                              <span className="text-[9px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">
                                {t.nb_non_lus}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate mb-1">
                            {t.autres_participants.join(', ') || 'Conversation'}
                          </div>
                          <div className="text-xs text-gray-400 line-clamp-2">{t.dernier_message}</div>
                          <div className="text-[10px] text-gray-600 mt-1">
                            {t.nb_messages} message{t.nb_messages > 1 ? 's' : ''}
                            {t.dernier_date && ` • ${new Date(t.dernier_date).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      )}

      {showNouveau && (
        <ModalNouveau onClose={() => setShowNouveau(false)}
          onSent={(tid) => { setShowNouveau(false); setThreadOuvert(tid); refetch(); }} />
      )}
    </FormateurLayout>
  );
}

function VueThread({ threadId, onBack }: { threadId: number; onBack: () => void }) {
  const { data, isLoading, refetch } = useThreadMessageInterneQuery(threadId);
  const [contenu, setContenu] = useState('');
  const [envoyer, { isLoading: sending }] = useEnvoyerMessageInterneMutation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data]);

  const premier = data?.results?.[0];
  const sujet = premier?.sujet || '(Sans sujet)';
  const destRole = premier?.destinataire_role as DestRole | undefined;

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contenu.trim() || !destRole) return;
    try {
      await envoyer({
        destinataire_role: destRole,
        contenu, thread_id: threadId,
      }).unwrap();
      setContenu('');
      refetch();
    } catch {}
  };

  return (
    <FormateurLayout title={sujet}>
      <button onClick={onBack}
        className="mb-2 text-[11px] text-gray-400 hover:text-white inline-flex items-center gap-1">
        <ChevronLeft size={12} /> Retour
      </button>

      <div ref={scrollRef} className="space-y-2 mb-3 max-h-[60vh] overflow-y-auto pr-1">
        {isLoading && (
          <div className="text-center py-6 text-gray-500 text-sm">
            <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
          </div>
        )}
        {(data?.results || []).map(m => (
          <div key={m.id} className={`flex ${m.a_moi ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-2.5 ${
              m.a_moi
                ? 'bg-amber-900/40 border border-amber-700/60 text-amber-50'
                : 'bg-gray-800 border border-gray-700 text-gray-100'
            }`}>
              {!m.a_moi && (
                <div className="text-[10px] text-gray-400 mb-0.5 font-semibold">
                  {m.expediteur_nom} ({m.expediteur_role})
                </div>
              )}
              <div className="text-xs whitespace-pre-wrap">{m.contenu}</div>
              <div className="text-[9px] mt-1 opacity-60">
                {new Date(m.date_envoi).toLocaleString('fr-FR', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handle}
        className="sticky bottom-0 bg-gray-900 border-t border-gray-800 -mx-3 px-3 py-3 flex gap-2">
        <input value={contenu} onChange={(e) => setContenu(e.target.value)}
          placeholder="Réponse…" maxLength={5000}
          className="flex-1 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
        <button type="submit" disabled={sending || !contenu.trim()}
          className="px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded">
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
        </button>
      </form>
    </FormateurLayout>
  );
}

function ModalNouveau({ onClose, onSent }: {
  onClose: () => void; onSent: (threadId: number) => void;
}) {
  const [dest, setDest] = useState<DestRole | null>(null);
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoyer, { isLoading }] = useEnvoyerMessageInterneMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!dest) { setErreur('Choisissez un destinataire'); return; }
    if (!contenu.trim()) { setErreur('Message requis'); return; }
    try {
      const res = await envoyer({
        destinataire_role: dest,
        sujet, contenu,
      }).unwrap();
      onSent(res.thread_id);
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md p-4 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base text-white">Nouveau message</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">Destinataire *</label>
          <div className="grid grid-cols-3 gap-1.5">
            {(['surveillance', 'direction', 'scolarite'] as const).map(d => {
              const meta = DEST_META[d];
              return (
                <button key={d} type="button" onClick={() => setDest(d)}
                  className={`px-2 py-2 text-xs rounded border inline-flex flex-col items-center gap-0.5 ${
                    dest === d
                      ? `bg-${meta.color}-600 text-white border-${meta.color}-600`
                      : 'bg-gray-800 text-gray-300 border-gray-700'
                  }`}>
                  <meta.Icon size={14} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">Sujet</label>
          <input value={sujet} onChange={(e) => setSujet(e.target.value)} maxLength={200}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">Message *</label>
          <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
            rows={5} required maxLength={5000}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
        </div>

        {erreur && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
            {erreur}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !contenu.trim() || !dest}
            className="px-4 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Envoyer
          </button>
        </div>
      </form>
    </div>
  );
}
